/**
 * Note Title Editor Component
 *
 * Displays and allows editing of the note title.
 * Features:
 * - Display mode: Large heading with tooltip
 * - Edit mode: TextField with check button
 * - Double-click to edit
 * - Enter key to save
 * - RTK Query mutation with optimistic update
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useUpdateNoteMutation } from '@features/notes';
import { Check as CheckIcon } from '@mui/icons-material';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import type { NoteResponse } from '@store/generated/notes';
import { useSnackbar } from 'notistack';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';

export interface NoteTitleEditorProps {
  /** Note ID for updating */
  noteId: string;
  /** Note data with title */
  noteData?: NoteResponse;
  /** Whether title editing is allowed */
  editable?: boolean;
}

/**
 * Note Title Editor Component
 * Simple h1-style heading that can be edited inline
 */
export const NoteTitleEditor: React.FC<NoteTitleEditorProps> = ({ noteId, noteData, editable = true }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [updateNote] = useUpdateNoteMutation();
  const { note: noteMetrics } = useEventTracking();

  // State for editing
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(noteData?.note_title || '');
  const [editStartTime, setEditStartTime] = useState<number | null>(null);

  const encounterId = noteData?.encounter?.encounter_id;

  // Sync editedTitle with noteData when it changes
  useEffect(() => {
    if (noteData?.note_title) {
      setEditedTitle(noteData.note_title);
    }
  }, [noteData?.note_title]);

  /**
   * Handle title update via RTK Query mutation
   */
  const handleUpdateTitle = useCallback(
    async (method: 'enter_key' | 'button_click') => {
      // Close the editing UI immediately
      setIsEditingTitle(false);

      // Check if title actually changed
      const titleChanged = editedTitle !== noteData?.note_title;

      try {
        // Use RTK Query mutation - onQueryStarted handles optimistic update
        await updateNote({
          noteId: noteId,
          updateNoteRequest: {
            note_title: editedTitle,
          },
        }).unwrap();

        // Track note edit (Phase 5: Copy/edit outcome events)
        if (titleChanged) {
          noteMetrics.edit(noteId, {
            edit_type: 'title',
            changes_count: 1,
            edit_method: method,
            title_length_before: noteData?.note_title?.length,
            title_length_after: editedTitle.length,
            encounter_id: encounterId,
          });
        }
      } catch (error) {
        console.error('Failed to update note title:', error);
        enqueueSnackbar('Failed to update note title', { variant: 'error' });
      }
    },
    [noteId, editedTitle, noteData?.note_title, updateNote, noteMetrics, enqueueSnackbar, encounterId],
  );

  /**
   * Handle edit cancellation
   */
  const handleCancelEdit = useCallback(() => {
    if (editStartTime) {
      const editDuration = Date.now() - editStartTime;
      noteMetrics.titleEditCancelled(noteId, {
        title_length_before: noteData?.note_title?.length,
        edit_duration_ms: editDuration,
        encounter_id: encounterId,
      });
    }
    setIsEditingTitle(false);
    setEditedTitle(noteData?.note_title || '');
    setEditStartTime(null);
  }, [noteId, editStartTime, noteData?.note_title, encounterId, noteMetrics]);

  /**
   * Handle edit start
   */
  const handleStartEdit = useCallback(() => {
    setIsEditingTitle(true);
    setEditStartTime(Date.now());
    noteMetrics.titleEditStarted(noteId, {
      title_length: noteData?.note_title?.length,
      encounter_id: encounterId,
    });
  }, [noteId, noteData?.note_title, encounterId, noteMetrics]);

  // Don't render if there's no title
  if (!noteData?.note_title) {
    return null;
  }

  return (
    <NoteSectionContainer sx={{ mb: 1, mt: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
        onDoubleClick={() => editable && !isEditingTitle && handleStartEdit()}
      >
        {isEditingTitle ? (
          <>
            {/* EDIT MODE */}
            <TextField
              value={editedTitle}
              onChange={e => setEditedTitle(e.target.value)}
              variant="standard"
              fullWidth
              autoFocus
              InputProps={{
                style: {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleUpdateTitle('enter_key');
                } else if (e.key === 'Escape') {
                  handleCancelEdit();
                }
              }}
              onBlur={handleCancelEdit}
            />
            <IconButton
              onClick={() => handleUpdateTitle('button_click')}
              size="small"
              sx={{ ml: 1 }}
              onMouseDown={e => e.preventDefault()} // Prevent blur before click
            >
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          /* DISPLAY MODE */
          <Tooltip title="Double click to edit title" arrow placement="right">
            <Typography
              variant="h1"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: {
                  xs: '1rem',
                  sm: '1.125rem',
                  md: '1.25rem',
                  lg: '1.5rem',
                },
                cursor: 'text',
              }}
            >
              {noteData.note_title}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </NoteSectionContainer>
  );
};
